/**
 * Floor (art §4.9: banded tiles, grout, aerial fade, hatched contact patches — the only shadow in the game; the floor
 * and the painted wall band end at z = −1.35 H outside the orbitable cloister) and the backdrops drawn last at z = 1 (art §4.5 lapis starry vault,
 * §4.6 diaper, §4.7 gold ground, §4.8 fresco, §4.11 vellum).
 */
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';
import tgpu from 'typegpu';
import { frameLayout } from '../gpu/layouts';
import {expeditionGround} from './expedition';
import { fbm2o, fbm3o, hatchLines, lineMask, ramp3, rnd2, rnd22, sdQuatrefoil, sdStar, vnoise, worley2 } from './lib';
import {
  P_BOLE, P_GLINT, P_GOLD, P_GOLD_DEEP, P_GOLD_LIGHT, P_INK, P_INTONACO, P_LAPIS, P_LAPIS_LIGHT, P_LAPIS_NIGHT,
  P_LEAD_WHITE, P_OCHRE, P_OCHRE_LIGHT, P_SINOPIA, P_UMBER, P_VELLUM, P_VELLUM_AGED, P_VERMILION, P_VERDIGRIS,
} from './pigments';

export const FLOOR_BACK_Z = -1.35;
const WALL_H = 0.06;

// ── floor ───────────────────────────────────────────────────────────────────────────────────────────────────────────

export const floorVertex = tgpu.vertexFn({
  in: { vid: d.builtin.vertexIndex },
  out: { position: d.builtin.position, world: d.vec3f, kind: d.interpolate('flat', d.u32) },
})((input) => {
  'use gpu';
  const quad = [d.vec2f(0, 0), d.vec2f(1, 0), d.vec2f(1, 1), d.vec2f(0, 0), d.vec2f(1, 1), d.vec2f(0, 1)];
  const q = quad[input.vid % 6];
  const kind = d.u32(std.floor(d.f32(input.vid) / 6.0));
  let w = d.vec3f(-9.0 + q.x * 18.0, 0.0, -1.35 + (1.0 - q.y) * 9.0);
  if (kind === 1) {
    // painted wall band rising at the back edge of the floor
    w = d.vec3f(-9.0 + q.x * 18.0, q.y * WALL_H, -1.35);
  }
  if(frameLayout.$.light.timing.z===6){
    // Hero Forge's orbit cameras cross the old front-only stage's back edge.
    // The cloister is a courtyard: ground continues underneath either side.
    w=d.vec3f(-20.0+q.x*40.0,0.0,-20.0+q.y*40.0);
    if(kind===1)w=d.vec3f(-20.0+q.x*40.0,0.0,-20.0);
  }
  return { position: frameLayout.$.frame.viewProj.mul(d.vec4f(w, 1)), world: w, kind };
});

export const floorFragment = tgpu.fragmentFn({
  in: { fragPos: d.builtin.position, world: d.vec3f, kind: d.interpolate('flat', d.u32) },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const sl = frameLayout.$.light;
  const mats = frameLayout.$.mats;
  const xz = input.world.xz;
  const fwXZ = std.fwidth(xz);
  const fwY = std.fwidth(input.world.y);
  const L = sl.keyDir.xyz;
  // the floor sits in the mid band (art §4.9 'lit and banded'), the light band is kept for figures
  const tN = std.select(L.y, L.z, input.kind === 1) * 0.5 + 0.5 - 0.16;
  // hand-laid tiles: the grid wobbles a little, each tile has its own tone, worn edges and tempera mottling
  const wob = d.vec2f(vnoise(xz.mul(3.1)) - 0.5, vnoise(xz.mul(3.1).add(d.vec2f(9.2, 4.4))) - 0.5).mul(0.018);
  const g = xz.add(wob).div(0.25);
  const tile = std.floor(g);
  const tf = std.fract(g);
  const n = vnoise(xz.mul(9.0)) * 0.6 + vnoise(xz.mul(23.0)) * 0.4;
  const big = fbm2o(xz.mul(1.7));
  const tj = (rnd2(tile) - 0.5) * 0.09;
  const t = tN + (n - 0.5) * 0.08 + tj;
  const T1 = sl.keyDir.w;
  const T2 = sl.keyTint.w;
  const a1 = std.smoothstep(T1 - 0.02, T1 + 0.02, t);
  const a2 = std.smoothstep(T2 - 0.02, T2 + 0.02, t);
  const checker = std.abs((tile.x + tile.y) % 2.0) < 0.5;
  const rA = ramp3(mats[21].xyz, mats[22].xyz, mats[23].xyz, a1, a2);
  const rB = ramp3(mats[24].xyz, mats[25].xyz, mats[26].xyz, a1, a2);
  let col = std.select(rB, rA, checker);
  col = col.mul(1.0 + (n - 0.5) * 0.14 + (big - 0.5) * 0.16 + tj);
  // worn edges: each tile darkens a little towards its rim
  const edgeD = std.min(std.min(tf.x, 1.0 - tf.x), std.min(tf.y, 1.0 - tf.y));
  col = col.mul(0.9 + 0.1 * std.smoothstep(0.0, 0.14 + 0.08 * n, edgeD));
  // grout: ink lines ~1.2 device px wide
  const fw = std.max(fwXZ.x, fwXZ.y) / 0.25;
  const gu = lineMask(g.x, fw * 1.3, fw);
  const gv = lineMask(g.y, fw * 1.3, fw);
  col = std.mix(col, P_INK.$, std.max(gu, gv) * 0.6);
  // a few cracked tiles
  const cr = worley2(xz.mul(5.5));
  const crack = (1.0 - std.smoothstep(0.0, fw * 0.25 + 0.012, cr.y)) * std.step(0.82, rnd2(tile.add(d.vec2f(7, 3))));
  col = std.mix(col, P_INK.$, crack * 0.45);
  if (sl.timing.z === 6) {
    // A finite courtyard exposes the painted distant landscape. An infinite
    // ground plane filled the orthographic sky with tiles, resembling a wall.
    if(xz.y< -2.3)std.discard();
    // Painted, deliberately foreshortened checker pavement. Uneven pigment
    // and misregistered ink keep the squares from looking like a shader grid.
    const fg = xz.add(wob.mul(.30)).div(d.vec2f(.22,1.02));
    const tileId = std.floor(fg);
    const ft = std.fract(fg);
    const fd = std.min(std.min(ft.x,1.0-ft.x),std.min(ft.y,1.0-ft.y));
    const aa = std.max(std.fwidth(fg.x),std.fwidth(fg.y));
    const red = std.abs((tileId.x+tileId.y)%2.0)<.5;
    const accent = std.abs((tileId.x-tileId.y)%7.0)<.5;
    const flag = std.select(std.select(P_VELLUM_AGED.$,P_VERMILION.$.mul(.8),red),P_VERDIGRIS.$.mul(.6),accent);
    const wear = fbm2o(xz.mul(d.vec2f(7,2.6)));
    col = flag.mul(.80+rnd2(tileId)*.17+(n-.5)*.13);
    col = std.mix(col,P_VELLUM_AGED.$,std.smoothstep(.69,.87,wear)*.43);
    col = std.mix(P_INK.$,col,std.smoothstep(.008,.008+aa*.65,fd));
    const inset=1.0-std.smoothstep(.009,.016+aa,std.abs(fd-.07));
    col=std.mix(col,P_INK.$,inset*.16);
    const scratch=(1.0-std.smoothstep(.015,.04,std.abs(vnoise(xz.mul(d.vec2f(68,4)))-.5)))*std.smoothstep(.58,.8,wear);
    col=std.mix(col,P_VELLUM_AGED.$,scratch*.23);
  }
  // contact patches: horizontal hatching in world space, an ellipse under each foot; shrinks and fades when airborne
  const count = sl.qualityFlags.y;
  let patch = d.f32(0);
  for (let i = d.u32(0); i < count; i++) {
    const c = sl.contact[i];
    const e = std.length(xz.sub(c.xy).div(d.vec2f(0.32, 0.12).mul(std.max(c.w / 0.16, 0.5))));
    const s = 1.0 / (1.0 + 2.0 * std.max(c.z, 0.0));
    const edge = 1.0 - std.smoothstep(s - 0.12, s, e);
    patch = std.max(patch, edge * s);
  }
  const hz = hatchLines(xz.y + n * 0.004, 1.0 / std.max(fwXZ.y, 0.00001), sl.hatch.y * 0.7, d.f32(1));
  col = std.mix(col, P_INK.$, hz * patch * 0.62);
  col = std.mix(col, P_INK.$, patch * std.select(d.f32(.12),d.f32(.38),sl.timing.z===6));
  // aerial fade with distance (no fog)
  col = std.mix(col, P_VELLUM.$, std.smoothstep(-0.4, -1.35, input.world.z) * 0.14);
  if (input.kind === 1) {
    // wall band: vermilion with an ink line on top
    const vb = std.mix(P_VERMILION.$.mul(0.62), P_VERMILION.$, a2 * 0.5 + 0.5).mul(1.0 + (n - 0.5) * 0.12);
    const top = 1.0 - std.smoothstep(WALL_H - fwY * 2.5, WALL_H - fwY * 1.0, input.world.y);
    col = std.mix(P_INK.$, vb, top);
    if(sl.timing.z === 6) col = d.vec3f(.18,.145,.105).mul(.8+.2*top);
  }
  if (sl.qualityFlags.w === 1) {
    col = std.select(std.select(d.vec3f(0.93, 0.88, 0.75), P_VELLUM_AGED.$, t < T2), d.vec3f(0.02, 0.04, 0.2), t < T1);
  }
  return d.vec4f(col, 0.0);
});

// ── backdrops ───────────────────────────────────────────────────────────────────────────────────────────────────────

export const skyVertex = tgpu.vertexFn({
  in: { vid: d.builtin.vertexIndex },
  out: { position: d.builtin.position },
})((input) => {
  'use gpu';
  const p = [d.vec2f(-1, -1), d.vec2f(3, -1), d.vec2f(-1, 3)];
  return { position: d.vec4f(p[input.vid], 1, 1) };
});

const rot2 = (p: d.v2f, a: number) => {
  'use gpu';
  const c = std.cos(a);
  const s = std.sin(a);
  return d.vec2f(p.x * c - p.y * s, p.x * s + p.y * c);
};

/** Gold-leaf colour for 2D grounds (stars, gold ground): banded ramp + leaf squares + crackle + diagonal sheen. */
export const leaf2d = (px: d.v2f, e: number, time: number, period: number) => {
  'use gpu';
  const leaf = rnd2(std.floor(px.div(26.0)));
  const ee = std.saturate(e + (leaf - 0.5) * 0.18 + (vnoise(px.mul(0.11)) - 0.5) * 0.15);
  const a = std.mix(P_GOLD_DEEP.$, P_GOLD.$, std.smoothstep(0.25, 0.45, ee));
  let g = std.mix(a, P_GOLD_LIGHT.$, std.smoothstep(0.62, 0.8, ee));
  const cr = worley2(px.mul(0.07));
  g = std.mix(g, P_BOLE.$, (1.0 - std.smoothstep(0.02, 0.06, cr.y)) * 0.35);
  const s = std.fract((px.x + px.y * 0.6) * 0.0012 - time / period);
  const sheen = std.exp(-std.pow((s - 0.5) / 0.035, 2.0)) * 0.55;
  return g.add(P_GLINT.$.mul(sheen));
};

const vault = (uv: d.v2f, px: d.v2f, aspect: number, time: number, scale: number, bits: number, picH: number) => {
  'use gpu';
  let base = std.mix(P_LAPIS.$, P_LAPIS_NIGHT.$, std.smoothstep(0.35, 1.0, uv.y) * 0.35);
  // tempera mottling: brush-scale blotches of lighter and darker lapis
  const m = fbm3o(uv.mul(d.vec2f(aspect, 1.0)).mul(3.2));
  const m2 = vnoise(uv.mul(d.vec2f(aspect, 1.0)).mul(14.0));
  base = base.mul(1.0 + (m - 0.5) * 0.42 + (m2 - 0.5) * 0.09);
  base = std.mix(base, P_LAPIS_LIGHT.$, std.smoothstep(0.58, 0.84, m) * 0.26);
  // brush-stroke streaks of the tempera (horizontal, like a laid-in ground)
  const streak = vnoise(d.vec2f(uv.x * aspect * 3.0, uv.y * 38.0));
  base = base.mul(1.0 + (streak - 0.5) * 0.08);
  // ultramarine granulation
  if ((bits & 64) !== 0) {
    base = base.mul(1.0 + (rnd2(std.floor(px.mul(0.75 / scale))) - 0.5) * 0.12);
  }
  // stars: staggered rows, ~7 per panel height
  let p = uv.mul(d.vec2f(aspect, 1.0)).mul(6.2);
  p = d.vec2f(p.x + 0.5 * (std.floor(p.y) % 2.0), p.y);
  const cell = std.floor(p);
  const f = std.fract(p);
  const h = rnd22(cell.add(d.vec2f(31, 17)));
  const h2 = rnd22(cell.add(d.vec2f(7, 93)));
  const c = d.vec2f(0.5).add(h.sub(0.5).mul(0.32));
  const aa = 1.3 * 6.2 / std.max(picH, 1.0);
  const q = rot2(f.sub(c), (h2.x - 0.5) * 0.5);
  const big = h2.y < 0.72;
  const rOut = std.select(d.f32(0.2), d.f32(0.34), big);
  // eight-pointed star: four long rays + four short ones (the keyart's gilt stars)
  const dLong = sdStar(q, d.f32(4), rOut * 0.16, rOut);
  const dShort = sdStar(rot2(q, 0.7853982), d.f32(4), rOut * 0.12, rOut * 0.55);
  const dStar = std.min(dLong, dShort);
  const present = std.step(0.18, h.x);
  const star = (1.0 - std.smoothstep(-aa, aa, dStar)) * present;
  const ring = (1.0 - std.smoothstep(aa * 0.4, aa * 1.8, std.abs(dStar))) * present;
  const tw = 0.85 + 0.15 * std.sin(time * 1.3 + h.x * 6.2831853);
  const g = leaf2d(px.div(scale), 0.55 + 0.25 * tw + (h.y - 0.5) * 0.2 + q.y * 0.6, time, d.f32(6));
  let col = std.mix(base, g, star);
  col = std.mix(col, P_INK.$, ring * 0.75 * (1.0 - star * 0.6));
  // lead-white dot triads in empty cells
  const pd = uv.mul(d.vec2f(aspect, 1.0)).mul(19.0);
  const hd = rnd22(std.floor(pd));
  const dd = std.length(std.fract(pd).sub(d.vec2f(0.5)).sub(hd.sub(0.5).mul(0.5)));
  const dot = (1.0 - std.smoothstep(0.045, 0.075, dd)) * std.step(0.9, hd.x) * (1.0 - star);
  col = std.mix(col, P_GOLD_LIGHT.$.mul(0.85), dot * 0.85);
  return col;
};

const goldGround = (uv: d.v2f, px: d.v2f, aspect: number, time: number, scale: number) => {
  'use gpu';
  const e = 0.55 + 0.1 * std.sin(uv.x * 2.0 + uv.y * 1.3);
  let g = leaf2d(px.div(scale), e, time, d.f32(5));
  // incised lozenge diaper (15 % darkening, no hue change)
  const q = rot2(uv.mul(d.vec2f(aspect, 1.0)).mul(9.0), 0.7853982);
  const l = std.max(lineMask(q.x, 0.04, 0.02), lineMask(q.y, 0.04, 0.02));
  g = g.mul(1.0 - l * 0.15);
  return g;
};

const fresco = (uv: d.v2f, aspect: number) => {
  'use gpu';
  const u = uv.mul(d.vec2f(aspect, 1.0));
  let base = std.mix(P_OCHRE_LIGHT.$, P_OCHRE.$, fbm2o(u.mul(4.0)));
  base = base.mul(1.0 - fbm2o(u.mul(1.5).add(d.vec2f(3))) * 0.12);
  const fl = fbm3o(u.mul(6.0).add(d.vec2f(9)));
  base = std.mix(base, P_INTONACO.$, std.smoothstep(0.62, 0.66, fl));
  base = std.mix(base, P_UMBER.$, (1.0 - std.smoothstep(0.0, 0.02, std.abs(fl - 0.64))) * 0.3);
  const cr = worley2(u.mul(9.0).add(d.vec2f(fbm2o(u.mul(3.0)) * 0.3)));
  base = std.mix(base, P_UMBER.$, (1.0 - std.smoothstep(0.0, 0.015, cr.y)) * 0.5);
  // painted border: a sinopia band near the top
  const band = std.smoothstep(0.86, 0.865, uv.y) * (1.0 - std.smoothstep(0.9, 0.905, uv.y));
  return std.mix(base, P_SINOPIA.$, band * 0.8);
};

const diaper = (uv: d.v2f, aspect: number, kind: number) => {
  'use gpu';
  const q = rot2(uv.mul(d.vec2f(aspect, 1.0)).mul(7.0), 0.7853982);
  const cell = std.floor(q);
  const f = std.fract(q).sub(d.vec2f(0.5));
  const A = P_VERMILION.$.mul(0.8);
  const B = P_LAPIS_LIGHT.$.mul(0.85);
  let col = std.select(B, A, std.abs((cell.x + cell.y) % 2.0) < 0.5);
  if (kind === 2) {
    const dq = sdQuatrefoil(f, 0.18, 0.2);
    col = std.select(A, B, dq < 0.0);
    col = std.mix(col, P_GOLD.$, 1.0 - std.smoothstep(0.0, 0.03, std.abs(dq)));
  } else {
    col = std.mix(col, P_GOLD.$, std.smoothstep(0.455, 0.47, std.max(std.abs(f.x), std.abs(f.y))));
    col = std.mix(col, P_LEAD_WHITE.$, 1.0 - std.smoothstep(0.06, 0.08, std.length(f)));
  }
  col = col.mul(1.0 + (vnoise(uv.mul(40.0)) - 0.5) * 0.08);
  return col;
};

/** Three thin, painted Gothic arches inside a rectangular illuminated page. */
const cloister = (uv: d.v2f, px: d.v2f, time: number) => {
  'use gpu';
  const paper = fbm2o(uv.mul(d.vec2f(13,10)));
  const brush = vnoise(uv.mul(d.vec2f(75,11)));
  const aa = 1.1*std.fwidth(uv.y);
  let col = P_INTONACO.$.mul(.82+paper*.14);
  const warp = (vnoise(uv.mul(d.vec2f(17,23)))-.5)*.0024;
  const x = (std.fract(uv.x*3.0)-.5)/3.0+warp;
  const y = uv.y-.70;
  const w = .148;
  const crown = std.max(std.length(d.vec2f(x-w,y)),std.length(d.vec2f(x+w,y)))-w*2;
  const arch = std.max(std.select(std.abs(x)-w,crown,y>0),.055-uv.y);
  const inside = 1.0-std.smoothstep(-aa,aa,arch);
  // Lapis is laid in broad streaks, with missing specks of pigment. No lit
  // night sky: these are gold stars painted onto the blue ground of the page.
  const wash=fbm3o(uv.mul(d.vec2f(8,6)));
  let lapis=std.mix(P_LAPIS.$,P_LAPIS_LIGHT.$,.24+wash*.25).mul(.77+paper*.3+(brush-.5)*.16);
  const cells=uv.mul(d.vec2f(9,8));
  const cell=std.floor(cells);
  const jitter=rnd22(cell.add(d.vec2f(7,23))).sub(.5).mul(.38);
  const starP=std.fract(cells).sub(.5).sub(jitter);
  const starR=.145+rnd2(cell.add(d.vec2f(4,9)))*.075;
  const rays=std.min(sdStar(starP,d.f32(4),starR*.13,starR),sdStar(rot2(starP,.7853982),d.f32(4),starR*.10,starR*.62));
  const present=std.step(.46,rnd2(cell));
  // Differentiate the continuous coordinate, not fract/jitter: a cell boundary
  // otherwise inflates the star edge to a full gold horizontal stripe.
  const starAA=std.fwidth(cells.y)*.7;
  const star=(1.0-std.smoothstep(-starAA,starAA,rays))*present;
  const outline=(1.0-std.smoothstep(starAA*.2,starAA*1.8,rays))*present;
  lapis=std.mix(lapis,P_INK.$,outline*.8);
  lapis=std.mix(lapis,P_GOLD_LIGHT.$.mul(.8+paper*.2),star);
  const chips=std.step(.986,rnd2(std.floor(px.mul(.6))))*std.smoothstep(.55,.8,paper);
  lapis=std.mix(lapis,P_VELLUM_AGED.$,chips*.46);
  col=std.mix(col,lapis,inside);
  // Two ink contours, an ochre hairline, white ribs and voussoir joints.
  const outer=1.0-std.smoothstep(aa,aa*2,std.abs(arch-.018));
  const inner=1.0-std.smoothstep(aa*.65,aa*1.5,std.abs(arch));
  const gilt=1.0-std.smoothstep(.001+aa,.002+aa,std.abs(arch-.007));
  col=std.mix(col,P_GOLD.$,gilt*.7);
  col=std.mix(col,P_INK.$,std.max(outer*.75,inner*.9));
  const archBand=std.smoothstep(.001,.004,arch)*(1.0-std.smoothstep(.018,.021,arch));
  const joint=1.0-std.smoothstep(.025,.05,std.abs(std.fract(std.atan2(y,std.abs(x)+w)*17.0)-.5));
  col=std.mix(col,P_UMBER.$,joint*archBand*std.step(.699,uv.y)*.66);
  // Slender paired columns carry carved capitals rather than a broad brick wall.
  const column=std.abs(std.abs(x)-.160);
  const shaft=(1.0-std.smoothstep(.010,.012,column))*std.step(.06,uv.y)*(1.0-std.step(.70,uv.y));
  const flute=std.cos(column*410.0)*.09;
  col=std.mix(col,P_INTONACO.$.mul(.64+paper*.13+flute),shaft);
  const shaftLine=(1.0-std.smoothstep(.0008,.002+aa,std.abs(column-.007)))*shaft;
  col=std.mix(col,P_INK.$,shaftLine*.66);
  const capital=(1.0-std.smoothstep(.021,.023,column))*(1.0-std.smoothstep(.011,.014,std.abs(uv.y-.70)));
  col=std.mix(col,P_GOLD.$.mul(.7+paper*.2),capital);
  const capLine=(1.0-std.smoothstep(.001,.002+aa,std.abs(std.abs(uv.y-.70)-.010)))*capital;
  col=std.mix(col,P_INK.$,capLine*.8);
  // Dry brush, fine broken scratches and low-frequency parchment abrasion.
  const scratch=(1.0-std.smoothstep(.012,.03,std.abs(brush-.5)))*std.smoothstep(.57,.82,paper);
  col=std.mix(col,P_VELLUM_AGED.$,scratch*.10);
  return col.mul(.97+rnd2(std.floor(px))*.06);
};

const vellumGround = (px: d.v2f) => {
  'use gpu';
  let col = std.mix(P_VELLUM.$, P_VELLUM_AGED.$, fbm2o(px.div(220.0)) * 0.5);
  col = col.mul(1.0 + (vnoise(px.mul(d.vec2f(0.9, 0.07))) - 0.5) * 0.04);
  return col;
};

export const skyFragment = tgpu.fragmentFn({
  in: { fragPos: d.builtin.position },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const sl = frameLayout.$.light;
  const pr = sl.panelRectPx;
  const px = input.fragPos.xy;
  // picture-interior uv with y up (0 at the bottom, 1 at the top)
  const uv = d.vec2f((px.x - pr.x) / pr.z, 1.0 - (px.y - pr.y) / pr.w);
  const aspect = pr.z / std.max(pr.w, 1.0);
  const time = sl.timing.x;
  const scale = sl.viewportPx.z;
  const id = d.u32(sl.timing.z);
  let col = d.vec3f(0);
  if (id === 0) {
    col = vault(uv, px, aspect, time, scale, sl.qualityFlags.z, pr.w);
  } else if (id === 3) {
    col = goldGround(uv, px, aspect, time, scale);
  } else if (id === 4) {
    col = fresco(uv, aspect);
  } else if (id === 6) {
    col = cloister(uv,px,time);
  } else if (id === 7) {
    col = expeditionGround(uv,px,time,sl.expedition);
  } else if (id === 5) {
    col = vellumGround(px.div(scale));
  } else {
    col = diaper(uv, aspect, id);
  }
  return d.vec4f(col, 0.0);
});

